import { useEffect, useState } from 'react';
import { fetchChangelog } from '../lib/api';
import { LoadingSpinner } from '../components/LoadingSpinner';

interface ChangelogEntry {
  id: string;
  date: string;
  title: string;
  description: string;
  version: string;
  created_at: string;
}

// Group changelog entries by version
function groupByVersion(entries: ChangelogEntry[]): Record<string, ChangelogEntry[]> {
  return entries.reduce((groups, entry) => {
    const version = entry.version;
    if (!groups[version]) {
      groups[version] = [];
    }
    groups[version].push(entry);
    return groups;
  }, {} as Record<string, ChangelogEntry[]>);
}

export default function Changelog() {
  const [changelog, setChangelog] = useState<ChangelogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadChangelog() {
      try {
        const data = await fetchChangelog();
        setChangelog(data);
      } catch (err) {
        setError('Failed to load changelog data. Please try again later.');
        console.error('Error loading changelog:', err);
      } finally {
        setLoading(false);
      }
    }

    loadChangelog();
  }, []);

  // Group entries by version
  const groupedEntries = groupByVersion(changelog);
  const versions = Object.keys(groupedEntries).sort((a, b) => {
    // Sort versions in descending order (newest first)
    return b.localeCompare(a, undefined, { numeric: true });
  });

  // Format date string
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-10">
      <h1 className="text-4xl font-bold text-pearl-white font-outfit mb-8 relative inline-block after:absolute after:bottom-0 after:left-0 after:w-3/4 after:h-px after:bg-gradient-to-r after:from-naia-pink after:to-transparent">Changelog</h1>
      
      {loading ? (
        <div className="flex justify-center py-20">
          <LoadingSpinner />
        </div>
      ) : error ? (
        <div className="bg-red-500/20 text-pearl-white p-4 rounded-lg">
          {error}
        </div>
      ) : changelog.length === 0 ? (
        <div className="text-slate-grey text-center py-12">
          No changelog entries found.
        </div>
      ) : (
        <div className="space-y-12 relative">
          {/* Timeline line that runs through all versions */}
          <div className="absolute left-[6px] top-8 bottom-0 w-0.5 bg-gradient-to-b from-naia-pink via-naia-pink/40 to-naia-pink/20"></div>
          {versions.map(version => (
            <div key={version} className="relative pl-6 group">
              <div className="relative mb-6">
                <div className="absolute -left-[30px] w-6 h-6 rounded-full bg-naia-pink ring-2 ring-deep-night shadow-lg shadow-naia-pink/40 flex items-center justify-center transition-all duration-300 group-hover:scale-110 group-hover:shadow-naia-pink/60">                  
                  <div className="w-2 h-2 bg-pearl-white rounded-full animate-pulse"></div>
                </div>
                <h2 className="text-2xl font-semibold text-pearl-white font-outfit flex items-center">
                  <span className="bg-naia-pink px-3 py-1 rounded-md text-pearl-white mr-3 shadow-md shadow-naia-pink/30 transition-all duration-300 group-hover:shadow-naia-pink/50 group-hover:translate-x-0.5">v{version}</span>
                  <span className="text-slate-grey text-sm">
                    {formatDate(groupedEntries[version][0].date)}
                  </span>
                </h2>
              </div>
              
              <div className="mt-6 space-y-6">
                {groupedEntries[version].map(entry => (
                  <div key={entry.id} className="relative">
                    <div className="absolute -left-[30px] w-4 h-4 rounded-full bg-naia-pink ring-2 ring-deep-night shadow-md shadow-naia-pink/40 transition-all duration-300 group-hover:scale-110"></div>
                    <div className="p-4 rounded-lg transition-all duration-300 hover:bg-white/5 group-hover:translate-x-1">
                      <h3 className="text-xl font-medium text-pearl-white">{entry.title}</h3>
                      <p className="mt-2 text-slate-grey whitespace-pre-line">{entry.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
